import { useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import Slider from 'react-slick';
import { Settings } from 'react-slick';
import cn from 'classnames';
// Api
import { GET_ARTICLES } from 'api/articles/queries';
// Types
import type {
  GetArticleForStore,
  GetArticleForStore_getArticleForStore_entities,
  GetArticleForStoreVariables,
} from 'api/articles/types/GetArticleForStore';
import {
  ArticleStatus,
  ArticleStoreFilterInput,
} from 'api/graphql-global-types';
// Ui
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
// Components
import SectionHeader from 'components/common3/SectionHeader/SectionHeader';
import NotFound from 'components/common/NotFound/NotFound';
import ContentCard, { ContentType } from 'ui3/Card/ContentCard';
import Carousel from 'ui3/Carousel/Carousel';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Hooks
import { useBreakpoints, useCarouselArrows } from 'hooks';
// Constants
import { ATHLETES } from 'constants/routes';
// Styles
import styles from './Articles.module.scss';

const PER_PAGE = 9;

const sliderSettings = {
  infinite: false,
  adaptiveHeight: false,
  speed: 500,
  swipeToSlide: true,
  variableWidth: true,
  slidesToScroll: 1,
  arrows: false,
  dots: false,
  centerPadding: '0px',
} as Settings;

type ArticlesProps = {
  storeId: string;
  className?: string;
  link?: string;
};

export const Articles = ({
  storeId,
  className,
  link,
}: ArticlesProps): JSX.Element | null => {
  const sliderRef = useRef<InstanceType<typeof Slider> | null>(null);
  const { isBreakpointL } = useBreakpoints();

  const inputVariables = useMemo(
    (): ArticleStoreFilterInput => ({
      storeId,
      statuses: [ArticleStatus.Active],
      limit: PER_PAGE,
      offset: 0,
    }),
    [storeId]
  );

  const { data, loading } = useQuery<
    GetArticleForStore,
    GetArticleForStoreVariables
  >(GET_ARTICLES, {
    variables: {
      input: inputVariables,
    },
    fetchPolicy: 'cache-and-network',
    skip: !storeId,
  });

  // A store-less article (a topic page) has no /{store}/news route to link to.
  const sponsoredArticles = (data?.getArticleForStore?.entities || []).filter(
    (article) => article.store?.slug
  );
  const total = data?.getArticleForStore?.total;
  const isNotFound = !loading && !total;

  const { canScroll, atStart, atEnd, onBeforeChange } = useCarouselArrows(
    sliderRef,
    sponsoredArticles.length
  );

  if (!storeId) {
    return null;
  }

  const seeAllLink = link ?? `${sponsoredArticles[0]?.store?.slug}/news`;

  const handlePrevClick = () => {
    sliderRef.current?.slickPrev();
  };

  const handleNextClick = () => {
    sliderRef.current?.slickNext();
  };

  // Mobile keeps the count-based threshold; desktop uses measurement.
  const showMobileArrows = sponsoredArticles.length > 2;

  const settings: Settings = {
    ...sliderSettings,
    beforeChange: onBeforeChange,
  };

  if (loading) {
    return <FourDotsLoader />;
  }

  if (isNotFound) {
    return <NotFound title="No Articles" />;
  }

  if (!sponsoredArticles.length) {
    return null;
  }

  return (
    <section className={cn(styles.section, className)}>
      <SectionHeader
        title="Latest Articles"
        onPrev={isBreakpointL ? handlePrevClick : undefined}
        onNext={isBreakpointL ? handleNextClick : undefined}
        seeAllLink={seeAllLink}
        seeAllLinkTitle="Read Articles"
        showArrows={isBreakpointL ? canScroll : false}
        prevDisabled={atStart}
        nextDisabled={atEnd}
      />
      <div className={styles.articlesListContainer}>
        <div className={styles.rootCarousel}>
          <Carousel ref={sliderRef} {...settings} className={styles.slider}>
            {sponsoredArticles.map(
              (article: GetArticleForStore_getArticleForStore_entities) => (
                <ContentCard
                  key={article.id}
                  type={ContentType.NEWS}
                  imageUrl={article.url || ''}
                  tzCode={null}
                  linkUrl={`${article.store?.slug}/news/${article.slug}`}
                  dateTime={article.createdAt || ''}
                  title={article.title || ''}
                  user={article.store?.storeDetails?.storeName || ''}
                  userUrl={
                    article.store?.slug ? `/${article.store?.slug}` : ATHLETES
                  }
                  isPlay={false}
                  isArticleCard
                  hideCategory
                />
              )
            )}
          </Carousel>
          {showMobileArrows && !isBreakpointL && (
            <div className={styles.mobileArrows}>
              <button
                className={styles.arrowButton}
                onClick={handlePrevClick}
                aria-label="Previous"
              >
                <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
              </button>
              <button
                className={styles.arrowButton}
                onClick={handleNextClick}
                aria-label="Next"
              >
                <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Articles;
